<script setup>
import { onBeforeMount, onBeforeUnmount, reactive, ref, watch } from "vue"
import { useHttpErrorsHandler } from "../../Composables/httpErrorsHandler"
import { useLocalStorage } from "../../Composables/useLocalStorage.js"
import { useDataManager } from "../../Composables/useDataManager.js"
import { useZohoInvoices } from "../../Composables/useZohoInvoices.js"
import _ from 'lodash'
import axios from "axios"
import DataTable from "../../Components/DataTable.vue"
import Modal from "../../Components/Modal.vue"
import FormSelectField from "../../Components/FormSelectField.vue"

const { httpErrorsHandler } = useHttpErrorsHandler()

const storageKey = "Shiftech_InvoiceZohoList"
const { saveToLocalStorage, loadFromLocalStorage } = useLocalStorage(storageKey)

const filters = reactive({
    zoho_customer_id: null,
    status: null,
})
const filtersApplied = ref(true)
const centers = ref([])
const errors = ref(null)

const {
    url,
    data: invoicesZoho,
    links,
    meta,
    loading,
    query,
    searchLoaded,
    sortingField,
    sortingFieldsOrder,
    changeSorting,
    search,
    loadData,
} = useDataManager({
    baseUrl: '/api/zoho-invoices',
    baseSearchUrl: '/api/search/zoho-invoices',
    defaultSortingField: 'due_date',
    defaultSortingFieldsOrder: {
        id: 'asc',
        due_date: 'desc',
    },
    filters,
    onError: (error) => httpErrorsHandler(error),
})

const debouncedSearch = _.debounce(search, 250)

const columns = ref([
    { label: 'Numéro facture', slot: 'invoice_number', class: 'col-1 fw-bold' },
    { label: 'Centre', slot: 'center', class: 'col-2' },
    { label: 'Émis le', slot: 'date', class: 'col-1' },
    { label: 'Échéance', slot: 'due_date', class: 'col-1' },
    { label: 'Statut', slot: 'status', class: 'col-1' },
    { label: 'Montant dû', slot: 'total', class: 'col-1' },
    { label: 'Détails', slot: 'details', class: 'col-1 text-center' },
    { label: 'Actions', slot: 'actions', class: 'col-1 text-center' },
])

onBeforeMount(() => {
    const savedSettings = loadFromLocalStorage()
    if (savedSettings) {
        sortingField.value = savedSettings.sortingField || sortingField.value
        Object.assign(sortingFieldsOrder, savedSettings.sortingFieldsOrder || sortingFieldsOrder)
        query.value = savedSettings.query || ''
        searchLoaded.value = savedSettings.searchLoaded || false
        if (savedSettings.filters) {
            Object.assign(filters, savedSettings.filters)
        }
        filtersApplied.value = savedSettings.filtersApplied !== false
    }

    loadData()
    getCenters()
})

watch(
    [sortingField, sortingFieldsOrder, query, searchLoaded],
    ([newSortingField, newSortingOrder, newQuery, newSearchLoaded]) => {
        saveToLocalStorage({
            sortingField: newSortingField,
            sortingFieldsOrder: newSortingOrder,
            query: newQuery,
            searchLoaded: newSearchLoaded,
            filters: { ...filters },
            filtersApplied: filtersApplied.value,
        })
    },
    { deep: true }
)

watch(
    () => [filters.zoho_customer_id, filters.status],
    () => {
        filtersApplied.value = false
    },
    { deep: true }
)

onBeforeUnmount(() => {
    document.querySelectorAll('.modal').forEach(modalElement => {
        const modalInstance = bootstrap.Modal.getInstance(modalElement)
        if (modalInstance) {
            modalInstance.hide()
        }
    })
})

const idToDelete = ref(null)

function showModal(modalId, id) {
    idToDelete.value = id
    const modale = bootstrap.Modal.getOrCreateInstance(document.getElementById(modalId))
    modale.show()
}

function hideModal(modalId) {
    const modale = bootstrap.Modal.getOrCreateInstance(document.getElementById(modalId))
    modale.hide()
}

const {
    deleteZohoInvoice,
} = useZohoInvoices({
    onDeleted: () => {
        loadData(url.value)
        hideModal('modalDelete')
    },
    onError: (error) => httpErrorsHandler(error),
})

function applyFilter() {
    filtersApplied.value = true
    saveToLocalStorage({
        url: url.value,
        sortingField: sortingField.value,
        sortingFieldsOrder: { ...sortingFieldsOrder },
        query: query.value,
        searchLoaded: searchLoaded.value,
        filters: { ...filters },
        filtersApplied: true,
    })
    loadData(url.value)
}

function getCenters() {
    centers.value = [{
        text: 'Tous les centres',
        value: null
    }]
    axios.get('/api/centers?sortingField=name&sortingOrder=asc&all==true')
        .then(response => {
            response.data.data.forEach(center => {
                if (center.data.zoho_customer_id !== null) {
                    centers.value.push({
                        text: center.data.name,
                        value: center.data.zoho_customer_id
                    })
                }
            })
        })
        .catch(error => {
            httpErrorsHandler(error)
        })
}
</script>

<template>
    <div class="row">
        <div class="col-lg-12">
            <div class="card">
                <div class="card-header">
                    <form>
                        <div class="row g-3">
                            <div class="col-xxl-5 col-sm-12">
                                <div class="search-box">
                                    <input type="search" class="form-control search" placeholder="Rechercher..." v-model="query" @input="debouncedSearch" id="searchZohoInvoices">
                                    <i class="ri-search-line search-icon"></i>
                                </div>
                            </div>

                            <form-select-field
                                name="filters.status"
                                label="Status"
                                :label-hidden="true"
                                @update:field="filters.status = $event"
                                :errors="errors"
                                :required="true"
                                :data="filters.status"
                                bs-class="col-xxl-3 col-sm-4"
                                :disabled="false"
                                additional-class="bg-light border-light"
                                placeholder="Tous les statuts"
                                :liste-options="[
                                    { text: 'Annulée', value: 'void' },
                                    { text: 'En retard', value: 'overdue' },
                                    { text: 'Envoyée', value: 'sent' },
                                    { text: 'Non payée', value: 'unpaid' },
                                    { text: 'Partiellement payée', value: 'partially_paid' },
                                    { text: 'Payée', value: 'paid' },
                                    { text: 'Vue', value: 'viewed' },
                                    { text: 'Tous les statuts', value: null },
                                ]"
                                margin="mb-0"
                            />

                            <form-select-field
                                name="filters.zoho_customer_id"
                                label="Centre"
                                :label-hidden="true"
                                @update:field="filters.zoho_customer_id = $event"
                                :errors="errors"
                                :required="true"
                                :data="filters.zoho_customer_id"
                                bs-class="col-xxl-3 col-sm-4"
                                :disabled="false"
                                additional-class="bg-light border-light"
                                placeholder="Tous les centres"
                                :liste-options="centers"
                                margin="mb-0"
                            />

                            <div class="col-xxl-1 col-sm-4">
                                <button type="button" class="btn w-100" @click.prevent="applyFilter" :class="filtersApplied ? 'btn-dark' : 'btn-warning'">
                                    <i class="ri-equalizer-fill me-1 align-bottom"></i>
                                    Filtrer
                                </button>
                            </div>
                        </div>
                    </form>
                </div>

                <div class="card-body">
                    <data-table
                        :columns="columns"
                        :data="invoicesZoho"
                        :loading="loading"
                        :links="links"
                        :meta="meta"
                        :sorting-field="sortingField"
                        :sorting-order="sortingFieldsOrder"
                        :search-loaded="searchLoaded"
                        @sort-changed="changeSorting"
                        @page-changed="loadData"
                    >
                        <!-- Invoice Number -->
                        <template #invoice_number="{ item }">
                            <span class="fw-medium text-dark">{{ item.data.invoice_number }}</span>
                        </template>

                        <!-- Center -->
                        <template #center="{ item }">
                            {{ item.data.center.data.name }}
                        </template>

                        <!-- Date -->
                        <template #date="{ item }">
                            {{ item.data.date_fr }}
                        </template>

                        <!-- Due Date -->
                        <template #due_date="{ item }">
                            {{ item.data.due_date_fr }}
                        </template>

                        <!-- Status -->
                        <template #status="{ item }">
                            <span :class="'badge fs-11 bg-' + item.data.status_badge_color + '-subtle text-' + item.data.status_badge_color">{{ item.data.status_fr }}</span>
                        </template>

                        <!-- Total -->
                        <template #total="{ item }">
                            {{ item.data.total }}
                        </template>

                        <!-- Details -->
                        <template #details="{ item }">
                            <a :href="item.data.zoho_invoice_url" target="_blank" class="btn btn-secondary bg-gradient waves-effect waves-light btn-sm">Accéder</a>
                        </template>

                        <!-- Actions -->
                        <template #actions="{ item }">
                            <!-- DELETE -->
                            <a href="#" class="text-danger" title="Supprimer la facture" @click.prevent="showModal('modalDelete', item.data.id)" v-if="item.can.delete">
                                <i class="ri-delete-bin-3-fill fs-17 px-1"></i>
                            </a>
                            <i class="ri-delete-bin-3-fill fs-17 px-1 text-muted" v-else></i>
                        </template>
                    </data-table>

                    <modal id="modalDelete" title="Supprimer la facture ?" btn-class="btn-danger" btn-text="Supprimer" @click:button="deleteZohoInvoice(idToDelete)">
                        <template #icon>
                            <lord-icon src="https://cdn.lordicon.com/gsqxdxog.json" trigger="loop" colors="primary:#212529,secondary:#f06548" style="width:90px;height:90px"></lord-icon>
                        </template>
                        <template #content>
                            <p class="text-muted fs-14 mb-4 pt-1">Vous êtes sur le point de supprimer une facture.</p>
                        </template>
                    </modal>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>

</style>
